/**
 * On-demand job posting translation (title + description).
 * Delegates to shared contentTranslation queue (rate-limit safe).
 */

import {
  normalizeContentLocale,
  translateTextBlocks,
} from './contentTranslation';

const MAX_DESC_CHARS = 12_000;

export interface JobTranslateFields {
  title?: string;
  description?: string;
}

export interface JobTranslatedFields {
  title?: string;
  description?: string;
}

/**
 * Translate job title and/or description for a target locale.
 * Returns originals on `en`, missing API key, or failure.
 */
export async function translateJobFields(
  jobId: string,
  locale: string,
  fields: JobTranslateFields,
  signal?: AbortSignal,
): Promise<JobTranslatedFields> {
  const normalized = normalizeContentLocale(locale);
  if (normalized === 'en') return { ...fields };

  const pending: Array<{ field: 'title' | 'description'; text: string }> = [];

  if (fields.title?.trim()) {
    pending.push({ field: 'title', text: fields.title.trim() });
  }

  if (fields.description?.trim()) {
    const text =
      fields.description.length > MAX_DESC_CHARS
        ? `${fields.description.slice(0, MAX_DESC_CHARS)}\n\n…`
        : fields.description;
    pending.push({ field: 'description', text });
  }

  if (pending.length === 0) return { ...fields };

  try {
    const translated = await translateTextBlocks(
      jobId,
      locale,
      pending.map((p) => p.text),
      'job posting title and description',
      signal,
    );
    const result: JobTranslatedFields = {};
    pending.forEach((p, i) => {
      result[p.field] = translated[i] ?? p.text;
    });
    return {
      title: result.title ?? fields.title,
      description: result.description ?? fields.description,
    };
  } catch {
    return { ...fields };
  }
}
